import {useVirtualizer} from '@tanstack/react-virtual';
import {type ReactNode, useCallback, useEffect, useId, useRef, useState} from 'react';
import {cn} from '../../lib/cn';
import type {SelectOption} from '../../lib/selector-option';
import {
  LegacySelectContent,
  LegacySelectItem,
  LegacySelectRoot,
  LegacySelectTrigger,
  LegacySelectValue,
} from './components';

type LegacyInfinitySelectProps = {
  label: string | ReactNode;
  className?: string;
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  itemHeight?: number;
  maxHeight?: number;
};

export function LegacyInfinitySelect({
  label,
  className,
  placeholder = 'Select option...',
  value,
  onValueChange,
  options,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  itemHeight = 35,
  maxHeight = 300,
}: LegacyInfinitySelectProps) {
  const id = useId();
  const parentRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const virtualizer = useVirtualizer({
    count: hasNextPage ? options.length + 1 : options.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight,
    overscan: 5,
  });

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    if (!virtualItems.length) return;

    const lastItem = virtualItems[virtualItems.length - 1];

    if (
      lastItem &&
      lastItem.index >= options.length - 5 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      loadMore();
    }
  }, [hasNextPage, isFetchingNextPage, loadMore, options.length, virtualItems]);

  const handleItemSelect = (selectedValue: string) => {
    onValueChange?.(selectedValue);
    setIsOpen(false);
  };

  return (
    <div className={cn('group relative min-w-[300px]', className)}>
      <label
        htmlFor={id}
        className="absolute start-1 top-0 z-10 block -translate-y-1/2 bg-white px-2 text-xs font-medium text-[var(--chekin-color-brand-navy)] group-has-[:disabled]:opacity-50"
      >
        {label}
      </label>
      <LegacySelectRoot
        value={value}
        onValueChange={handleItemSelect}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <LegacySelectTrigger id={id}>
          <LegacySelectValue placeholder={placeholder} />
        </LegacySelectTrigger>
        <LegacySelectContent>
          <div
            ref={parentRef}
            style={{
              height: `${Math.min(maxHeight, virtualizer.getTotalSize())}px`,
              overflow: 'auto',
            }}
          >
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map(virtualItem => {
                const isLoading = virtualItem.index >= options.length;
                const option = options[virtualItem.index];

                return (
                  <div
                    key={virtualItem.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    {isLoading || !option ? (
                      <div className="flex h-full items-center justify-center">
                        <div className="flex items-center gap-2 text-sm text-[var(--chekin-color-gray-1)]">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          Loading more...
                        </div>
                      </div>
                    ) : (
                      <LegacySelectItem
                        value={String(option.value)}
                        disabled={option.isDisabled || option.readOnly}
                      >
                        {option.label}
                      </LegacySelectItem>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </LegacySelectContent>
      </LegacySelectRoot>
    </div>
  );
}
export type {LegacyInfinitySelectProps};
